import modularScale from 'styles/helpers/modular-scale';
import styled, { css } from 'styled-components/macro';

import { ReactComponent as Caret } from 'icons/icon-toggle-caret.svg';

import Box from 'components/elements/Box';
import Row from 'components/data-visualization/Table/Row';
import { Meta } from 'components/Metadata/Metadata.style';
import EntityItem from 'components/base/EntityItem/EntityItem';
import {
  ArtworkWrapper,
  EntityMetadata,
  EntityTitle,
  StyledArtwork,
  Tag,
} from 'components/base/EntityItem/EntityItem.style';
import {
  ValueWrapper,
  CellContent as MetricCellContent,
} from 'components/data-visualization/Table/DoubleMetricCell.style';

export const StyledCaretWrap = styled(Box)`
  min-height: 3.9em;
  display: flex;
  align-items: center;
  @media screen and (max-width: 50em) {
    padding-right: 1.2em;
  }
`;

export const TableRow = styled(Row)<any>`
  display: flex;
  cursor: pointer;
  padding: 0 ${({ theme }): string => theme.spacing.xxsmall};
  border-bottom: 1px solid ${({ theme }): string => theme.LightGrey};
  border-top: ${({ isTopList, theme }): any =>
    isTopList && `1px solid ${theme.LightGrey};`};
  background: ${({ theme, isExpanded }): string =>
    isExpanded ? theme.Sand : 'transparent'};

  ${({ isTopList }): any =>
    isTopList &&
    css`
      ${StyledCaretWrap} {
        @media screen and (max-width: 50em) {
          padding-right: 0.2em;
        }
      }
    `};

  &:hover {
    background: ${({ theme }): string => theme.Sand};
  }
`;

export const Cell = styled('td')<any>`
  display: flex;
  align-items: ${({ open }): string => (open ? 'start' : 'center')};

  @media screen and (min-width: 50em) {
    flex-basis: 15%;
    justify-content: center;
  }
`;

export const CellContent = styled('td')`
  flex-grow: 1;
  padding: ${({ theme }): string =>
    `${theme.spacing.small} ${theme.spacing.xxsmall}`};
  @media screen and (min-width: 50em) {
    flex-grow: 70%;
  }
`;

export const StyledEntityItem = styled(EntityItem)<any>`
  ${({ isTopList }): any =>
    isTopList &&
    css`
      align-items: flex-start;
    `};

  ${ArtworkWrapper} {
    @media screen and (max-width: 25em) {
      width: 40px !important;
      margin-right: ${({ theme }): string => theme.spacing.xsmall};
      ${StyledArtwork} {
        width: 40px !important;
        height: 40px !important;
      }
    }
    margin-right: ${({ theme }): string => theme.spacing.medium};
    justify-content: initial;
  }

  ${EntityMetadata} {
    @media screen and (max-width: 25em) {
      max-width: ${({ isTopList }): string =>
        isTopList ? '100%' : '11.25rem'};
      padding-right: ${({ isTopList }): string => (isTopList ? '1rem' : '0')};
    }
    @media screen and (max-width: 50em) {
      & > div {
        line-height: 1;

        margin-bottom: ${({ isTopList }): string =>
          isTopList ? '0.2rem' : '0'};
      }
    }
  }

  ${Meta} {
    margin-bottom: ${({ theme }): string => theme.spacing.xsmall};
    @media screen and (max-width: 50em) {
      line-height: ${({ isTopList }): string => (isTopList ? '1.2' : '1')};
      margin: 0;
      span {
        font-size: ${({ isTopList }): string => (isTopList ? '13px' : '10px')};
      }
    }
    ${({ isTopList, isExpanded }): any =>
      !isTopList &&
      css`
        span {
          &:not(:first-of-type) {
            display: ${isExpanded ? 'inline-block' : 'none'};
          }
        }
      `}

    ${({ isTopList }): any =>
      isTopList &&
      css`
        .versionsSpan {
          display: block;

          &:before {
            display: none;
          }
        }
      `}
  }
  ${EntityTitle} {
    @media screen and (max-width: 50em) {
      font-size: ${({ isTopList }): string => (isTopList ? '16px' : '13px')};
      align-items: ${({ isTopList }): string =>
        isTopList ? 'flex-start' : 'center'};
    }
  }
  ${ValueWrapper} {
    font-size: 11px;
    font-weight: 600;
    text-align: left;
    color: ${({ theme }): string => theme.ShadowBlack};
    &:not:first-of-type {
      margin-right: 0;
    }
    @media screen and (min-width: 50em) {
      font-size: 12px;
      margin-right: ${({ theme }): string => theme.spacing.medium};
    }
  }
  ${MetricCellContent} {
    flex-flow: row wrap;
    @media screen and (min-width: 50em) {
      justify-content: start;
    }
  }
  ${Tag} {
    transform: translateY(0);
    font-size: 9px;
    letter-spacing: 0.82px;
  }
`;

export const ChangeArrow = styled('span')<any>`
  color: ${({ isPositive, theme }): string =>
    isPositive ? theme.PositiveChange : theme.NegativeChange};
  visibility: ${({ isExpanded }): string =>
    isExpanded ? 'hidden' : 'initial'};

  ${({ isStretched }): any =>
    isStretched &&
    css`
      min-height: 3.9em;
      display: flex;
      align-items: center;
    `};
`;

export const StyledCaret = styled(Caret)<any>`
  transition: ${({ theme }): string =>
    `transform ${theme.transitionDuration} ${theme.timingFunction}`};
  transform: ${({ open }): string => (open ? 'rotate(180deg)' : 'rotate(0)')};
`;

export const Metric = styled('p')<any>`
  margin: 0;
  font-size: ${modularScale(-2)};
  text-transform: ${({ isUpperCase }): string =>
    isUpperCase ? 'uppercase' : 'initial'};
  font-weight: 600;
  color: ${({ theme, hasColor }): string =>
    hasColor ? theme[hasColor] : theme.ShadowBlack};
  @media screen and (min-width: 50em) {
    font-size: ${modularScale(-1)};
  }
`;

export const MetaDetails = styled(Meta)<any>`
  margin: 0 ${({ theme }): string => theme.spacing.xsmall} 0 0;
  font-size: ${modularScale(-2)};
  text-transform: ${({ isUpperCase }): string =>
    isUpperCase ? 'uppercase' : 'initial'};
  line-height: 1.6;
  @media screen and (min-width: 50em) {
    font-size: ${modularScale(-1)};
  }
`;

export const MetricsList = styled('ul')`
  padding: ${({ theme }): string => theme.spacing.xsmall} 0;
`;

export const ExpandedMetrics = styled('div')`
  ${Box} > ${Box} {
    @media screen and (min-width: 25em) {
      flex: 0 0 64px;
      padding-right: 1.2rem;
    }
    @media screen and (min-width: 50em) {
      flex: 0 0 63px;
      padding-right: 1.1rem;
    }
  }
`;

export const MetricBox = styled.div<any>`
  display: flex;

  ${MetaDetails}, ${Metric} {
    font-size: ${({ isTopList }): any => isTopList && '13px'};
  }
`;
